import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MyAssignments, MyCertificates } from "@/components/MyAssignments";
import MyEvaluationsWidget from "@/components/MyEvaluationsWidget";
import PointsWidget from "@/components/PointsWidget";
import SectionHeader from "@/components/SectionHeader";
import RecentAchievements from "@/components/RecentAchievements";
import { useT } from "@/lib/i18n";
import { BookOpen, CheckCircle2, Trophy, FileText, PlayCircle, Library, ChevronRight } from "lucide-react";

function StatTile({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[#D1D5DB] rounded-md p-5 flex items-center gap-4">
      <div className="h-10 w-10 rounded-md bg-[#F3F4F6] flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#1B2E7A]" strokeWidth={1.5} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">{label}</p>
        <p className="font-heading text-2xl text-[#1A1A1A]">{value}</p>
      </div>
    </div>
  );
}

function CourseCard({ course, idx }) {
  return (
    <Link
      to={`/courses/${course.id}`}
      data-testid={`course-card-${course.id}`}
      className={`fade-up stagger-${(idx % 6) + 1} group bg-white border border-[#D1D5DB] rounded-md overflow-hidden hover:shadow-md hover:border-[#D1D5DB] hover:-translate-y-[2px] transition-all duration-200 flex flex-col`}
    >
      <div className="aspect-[16/9] bg-[#F3F4F6] overflow-hidden">
        {course.cover_url ? (
          <img
            src={course.cover_url}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
          />
        ) : (
          <div className="h-full flex items-center justify-center text-[#6B7280]">
            <BookOpen className="h-10 w-10" strokeWidth={1.2} />
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280] mb-2">{course.topic}</p>
        <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2 leading-snug">
          {course.title}
        </h3>
        <p className="text-sm text-[#374151] leading-relaxed line-clamp-2 mb-4">
          {course.description}
        </p>
        <div className="mt-auto">
          <div className="flex items-center gap-3 text-xs text-[#737373] mb-3">
            <span className="inline-flex items-center gap-1"><PlayCircle className="h-3.5 w-3.5" strokeWidth={1.5} />{course.lessons_count} lecciones</span>
            <span className="inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" strokeWidth={1.5} />{course.pdfs_count} PDFs</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#E5E7EB] overflow-hidden">
            <div
              className="h-full bg-[#1B2E7A] transition-all duration-500"
              style={{ width: `${course.progress_pct || 0}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-[#737373]">{course.progress_pct || 0}% avanzado</span>
            {course.passed && (
              <span className="inline-flex items-center gap-1 text-xs text-[#1B2E7A] font-medium">
                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} /> Aprobado · {course.best_score}%
              </span>
            )}
            {!course.passed && course.best_score != null && (
              <span className="inline-flex items-center gap-1 text-xs text-[#DC2626] font-medium">
                Mejor: {course.best_score}%
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const t = useT();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/courses");
        setCourses(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const completed = courses.filter((c) => c.passed).length;
  const totalAttempted = courses.filter((c) => c.best_score != null).length;
  const avgScore = totalAttempted
    ? Math.round(
        courses.filter((c) => c.best_score != null).reduce((s, c) => s + c.best_score, 0) /
          totalAttempted
      )
    : 0;

  const weakAreas = courses
    .filter((c) => c.best_score != null && c.best_score < 70)
    .slice(0, 3);

  return (
    <div data-testid="dashboard-page">
      <div className="mb-10 fade-up">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2">
          {t("dashboard.hero.greeting", { name: user?.name?.split(" ")[0] || "" })}
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-3">
          {t("dashboard.hero.title")}
        </h1>
        <p className="text-[#374151] max-w-2xl leading-relaxed">
          {t("dashboard.hero.body")}
        </p>
        <div className="mt-5">
          <RecentAchievements />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 fade-up stagger-1">
        <StatTile label={t("dashboard.kpi.available_courses")} value={courses.length} icon={BookOpen} />
        <StatTile label={t("dashboard.kpi.passed_courses")} value={completed} icon={CheckCircle2} />
        <StatTile label={t("dashboard.kpi.exam_average")} value={`${avgScore}%`} icon={Trophy} />
      </div>

      <div className="border-t-2 border-[#D1D5DB] pt-10 mb-10">
        <PointsWidget />
      </div>

      {weakAreas.length > 0 && (
        <div className="bg-[#FFF6EE] border border-[#F2D9C2] rounded-md p-5 mb-10 fade-up stagger-2">
          <p className="text-xs uppercase tracking-[0.2em] text-[#DC2626] mb-2">{t("dashboard.weak.eyebrow")}</p>
          <p className="text-sm text-[#374151]">
            {t("dashboard.weak.recommend")}&nbsp;
            <span className="font-medium text-[#1A1A1A]">
              {weakAreas.map((c) => c.title).join(" · ")}
            </span>
          </p>
        </div>
      )}

      <div className="border-t-2 border-[#D1D5DB] pt-10">
        <MyEvaluationsWidget />
        <MyAssignments />
        <MyCertificates />
      </div>

      <Link
        to="/biblioteca"
        data-testid="library-cta"
        className="group block bg-gradient-to-br from-[#1B2E7A] to-[#13205A] text-white rounded-md p-6 mb-10 hover:shadow-lg transition-all fade-up stagger-3"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-md bg-white/10 flex items-center justify-center">
              <Library className="h-6 w-6 text-white" strokeWidth={1.4} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-1">Recursos del equipo</p>
              <h3 className="font-heading text-xl">Consulta los manuales y playbooks</h3>
              <p className="text-sm text-white/75 mt-1">
                Manuales oficiales, guías rápidas y estrategias NextGen reunidas como una biblioteca.
              </p>
            </div>
          </div>
          <ChevronRight className="h-6 w-6 text-white/70 group-hover:translate-x-1 transition-transform" />
        </div>
      </Link>

      <SectionHeader
        eyebrow={t("dashboard.eyebrow.catalog")}
        title={t("dashboard.title.modules")}
        testid="section-sales-catalog"
      />

      {loading ? (
        <div className="text-[#374151]">{t("dashboard.loading_courses")}</div>
      ) : courses.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#D1D5DB] rounded-md">
          <BookOpen className="h-10 w-10 text-[#6B7280] mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-[#374151]">{t("dashboard.empty_courses")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((c, i) => (
            <CourseCard key={c.id} course={c} idx={i} />
          ))}
        </div>
      )}
    </div>
  );
}
